import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const BOOKING_STATUSES = ["NEW", "REVIEWING", "AWAITING DEPOSIT", "CONFIRMED", "COMPLETED", "CANCELLED"];
const PRODUCT_STATUSES = ["ACTIVE", "SOLD_OUT", "COMING_SOON", "HIDDEN"];
const CONTACT_STATUSES = ["NEW", "READ", "FOLLOW UP", "RESOLVED"];
const WHOLESALE_STATUSES = ["NEW", "REVIEWING", "CONTACTED", "APPROVED", "DECLINED", "ARCHIVED"];
const PACK_STATUSES = ["DRAFT", "ACTIVE", "SOLD_OUT", "HIDDEN"];
const PAGE_STATUSES = ["DRAFT", "PUBLISHED"];
const WHOLESALE_ORDER_STATUSES = ["PENDING PAYMENT", "INVOICED", "PAID", "FULFILLED", "CANCELLED"];

const downloadCsv = async (path, filename) => {
    try {
        const res = await api.get(path, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${filename} downloaded.`);
    } catch {
        toast.error("Export failed.");
    }
};

const inputCls = "w-full rounded-xl border border-zinc-300 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-pink";
const labelCls = "block text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-1";
const btnGhost = "rounded-full border border-zinc-300 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:border-pink";
const btnDanger = "rounded-full border border-zinc-300 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-red-500 hover:border-red-400";

const TEST_EMAIL_RE = /@(example\.(com|org)|showtime\.nyc|deli\.com|school\.org)$/i;
const isTestRecord = (email) => TEST_EMAIL_RE.test(email || "");
const VALID_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const mailto = (email, subject) => `mailto:${email}?subject=${encodeURIComponent(subject)}`;

const TestPill = () => (
    <span className="inline-block rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-amber-100 text-amber-700" title="Looks like demo/test data">Test?</span>
);

const useFetch = (path, dep = 0) => {
    const [data, setData] = useState([]);
    const load = () => api.get(path).then((r) => setData(r.data || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    useEffect(() => { load(); }, [dep]); // eslint-disable-line
    return [data, load];
};

const StatusPill = ({ value }) => (
    <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase ${
        ["NEW", "paid", "ACTIVE", "subscribed"].includes(value) ? "bg-pink-soft text-pink-dark"
        : ["CONFIRMED", "COMPLETED", "APPROVED", "PUBLISHED"].includes(value) ? "bg-green-100 text-green-700"
        : ["CANCELLED", "failed", "HIDDEN", "DECLINED", "unsubscribed"].includes(value) ? "bg-zinc-200 text-zinc-500"
        : ["DRAFT"].includes(value) ? "bg-amber-100 text-amber-700"
        : "bg-zinc-100 text-zinc-600"
    }`}>{value}</span>
);

export const BookingsTab = () => {
    const [rows, reload] = useFetch("/admin/booking-requests");
    const setStatus = async (id, status) => {
        await api.patch(`/admin/booking-requests/${id}`, { status });
        toast.success(`Status → ${status}`);
        reload();
    };
    const remove = async (r) => {
        if (!window.confirm(`Permanently delete booking request ${r.request_number}? This cannot be undone.`)) return;
        await api.delete(`/admin/booking-requests/${r.id}`);
        toast.success("Booking request deleted.");
        reload();
    };
    return (
        <div className="space-y-4" data-testid="admin-bookings-tab">
            {rows.length === 0 && <p className="text-zinc-400 text-sm">No booking requests yet.</p>}
            {rows.map((r) => (
                <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`admin-booking-${r.request_number}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="font-oswald uppercase text-lg">{r.request_number} — {r.event_type} {isTestRecord(r.email) && <TestPill />}</p>
                            <p className="text-xs text-zinc-500">{r.name} · {r.email} · {r.phone}</p>
                            <p className="text-xs text-zinc-500 mt-1">{r.event_date} {r.start_time} · {r.duration} · {r.guests} guests · {r.setting} · {r.treats}</p>
                            <p className="text-xs text-zinc-500">{r.address}</p>
                            {(r.flavor_cotton_candy?.length > 0 || r.flavor_popcorn?.length > 0) && (
                                <p className="text-xs text-pink-dark mt-1">
                                    {r.flavor_cotton_candy?.length > 0 && `CC: ${r.flavor_cotton_candy.join(", ")}`}
                                    {r.flavor_cotton_candy?.length > 0 && r.flavor_popcorn?.length > 0 && " · "}
                                    {r.flavor_popcorn?.length > 0 && `PC: ${r.flavor_popcorn.join(", ")}`}
                                </p>
                            )}
                            {r.notes && <p className="text-xs text-zinc-400 mt-1">"{r.notes}"</p>}
                        </div>
                        <div className="flex items-center gap-2">
                            <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`admin-booking-status-${r.request_number}`} className={inputCls + " w-48"}>
                                {BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                            <button onClick={() => remove(r)} data-testid={`admin-booking-delete-${r.request_number}`} className={btnDanger}>Delete</button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

const FULFILLMENT_STATUSES_BY_METHOD = {
    local_delivery: ["Processing", "Ready for Delivery", "Out for Delivery", "Delivered", "Cancelled"],
    shipping: ["Processing", "Shipped", "Delivered", "Cancelled"],
    pickup: ["Processing", "Ready for Delivery", "Delivered", "Cancelled"],
};

export const OrdersTab = () => {
    const [rows, reload] = useFetch("/admin/orders");
    const [tracking, setTracking] = useState({});
    const setField = async (id, patch, msg) => {
        try {
            await api.patch(`/admin/orders/${id}`, patch);
            toast.success(msg || "Saved.");
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };
    const remove = async (o) => {
        if (!window.confirm(`Permanently delete order ${o.order_number}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/orders/${o.id}`);
            toast.success("Order deleted.");
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };
    return (
        <div className="space-y-4" data-testid="admin-orders-tab">
            <div className="flex justify-end">
                <button onClick={() => downloadCsv("/admin/export/orders.csv", "orders.csv")} data-testid="orders-export-csv" className={btnGhost}>Export CSV (CBEDC)</button>
            </div>
            {rows.length === 0 && <p className="text-zinc-400 text-sm">No orders yet.</p>}
            {rows.map((o) => (
                <div key={o.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`order-row-${o.order_number}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="font-oswald uppercase text-lg">
                                {o.order_number} — ${(o.total ?? o.subtotal)?.toFixed(2)} {isTestRecord(o.email || o.retailer_email) && <TestPill />}
                                {o.order_type === "wholesale" && <span className="inline-block rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-ink text-white align-middle ml-2">Wholesale</span>}
                            </p>
                            <p className="text-xs text-zinc-500">
                                {o.order_type === "wholesale"
                                    ? <>{o.business_name || "—"} · {o.retailer_email} · {[o.partner, o.territory, o.cohort].filter(Boolean).join(" · ") || "no CBEDC tags"}</>
                                    : <>{o.customer_name || "—"} · {o.email || "no email"}</>}
                                {" · "}{o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">{o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}</p>
                            {o.order_type === "wholesale" && o.notes && <p className="text-xs text-zinc-400 mt-1">"{o.notes}"</p>}
                            {o.order_type !== "wholesale" && (
                                <p className="text-xs text-zinc-500 mt-1 font-semibold">
                                    {o.fulfillment === "local_delivery" ? "LOCAL DELIVERY" : o.fulfillment === "shipping" ? "SHIPPING" : (o.fulfillment || "").toUpperCase()}
                                    {o.address && ` · ${o.address}`}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <StatusPill value={o.order_type === "wholesale" ? o.status : o.payment_status} />
                            <select value={o.status} onChange={(e) => setField(o.id, { status: e.target.value })} className={inputCls + " w-44"} data-testid={`order-status-${o.order_number}`}>
                                {(o.order_type === "wholesale" ? WHOLESALE_ORDER_STATUSES : ["initiated", "paid", "fulfilled", "cancelled", "archived"]).map((s) => <option key={s}>{s}</option>)}
                            </select>
                            <select
                                value={o.fulfillment_status || "Processing"}
                                onChange={(e) => setField(o.id, { fulfillment_status: e.target.value }, "Fulfillment updated.")}
                                className={inputCls + " w-44"}
                                data-testid={`order-fulfillment-${o.order_number}`}
                            >
                                {(FULFILLMENT_STATUSES_BY_METHOD[o.fulfillment] || FULFILLMENT_STATUSES_BY_METHOD.shipping).map((s) => <option key={s}>{s}</option>)}
                            </select>
                            {o.order_type !== "wholesale" && (
                                <button onClick={() => setField(o.id, { status: "archived" }, "Order archived.")} data-testid={`admin-order-archive-${o.order_number}`} className={btnGhost}>Archive</button>
                            )}
                            {o.payment_status !== "paid" && !(o.order_type === "wholesale" && ["PAID", "FULFILLED"].includes(o.status)) && (
                                <button onClick={() => remove(o)} data-testid={`admin-order-delete-${o.order_number}`} className={btnDanger}>Delete</button>
                            )}
                        </div>
                    </div>
                    {o.fulfillment === "shipping" && o.payment_status === "paid" && (
                        <div className="mt-3 flex items-center gap-2">
                            <input
                                value={tracking[o.id] ?? o.tracking_number ?? ""}
                                onChange={(e) => setTracking((t) => ({ ...t, [o.id]: e.target.value }))}
                                placeholder="Tracking number"
                                className={inputCls + " max-w-xs"}
                                data-testid={`order-tracking-${o.order_number}`}
                            />
                            <button onClick={() => setField(o.id, { tracking_number: tracking[o.id] ?? "" }, "Tracking saved.")} data-testid={`order-tracking-save-${o.order_number}`} className={btnGhost}>Save Tracking</button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const CrudTab = ({ path, fields, listKey, testid, subtitle }) => {
    const [rows, reload] = useFetch(path);
    const [form, setForm] = useState({});
    const editing = !!form.id;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

    const save = async () => {
        try {
            await api.post(path, form);
            toast.success("Saved.");
            setForm({});
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this record? This cannot be undone.")) return;
        try {
            await api.delete(`${path}/${id}`);
            toast.success("Deleted.");
            reload();
        } catch (e) {
            if (e.response?.status === 409) {
                if (window.confirm(`${formatApiError(e.response.data.detail)}\n\nDelete anyway?`)) {
                    await api.delete(`${path}/${id}?force=true`);
                    toast.success("Deleted.");
                    reload();
                }
            } else {
                toast.error(formatApiError(e.response?.data?.detail));
            }
        }
    };

    return (
        <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]" data-testid={testid}>
            <div className="space-y-3">
                {rows.map((r) => (
                    <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-4 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-oswald uppercase">{r[listKey]} {r.needs_pricing && <span className="inline-block rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-amber-100 text-amber-700 align-middle">Needs Pricing</span>}</p>
                            {subtitle ? (
                                <p className="text-xs mt-1 flex items-center gap-2 flex-wrap">{subtitle(r)}</p>
                            ) : (
                                <p className="text-xs text-zinc-500 truncate">
                                    {r.price != null && `$${Number(r.price).toFixed(2)} · `}{r.category || r.guests || r.slug || ""} {r.status && <StatusPill value={r.status} />} {r.custom_quote && <span className="inline-block rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-zinc-100 text-zinc-600">Custom Quote</span>}
                                </p>
                            )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button onClick={() => setForm(r)} data-testid={`${testid}-edit-${r.id}`} className={btnGhost}>Edit</button>
                            <button onClick={() => remove(r.id)} data-testid={`${testid}-delete-${r.id}`} className={btnDanger}>Delete</button>
                        </div>
                    </div>
                ))}
                {rows.length === 0 && <p className="text-zinc-400 text-sm">Nothing here yet.</p>}
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 h-fit">
                <p className="font-oswald uppercase text-lg mb-4">{editing ? "Edit" : "Add New"}</p>
                <div className="space-y-3">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className={labelCls}>{f.label}</label>
                            {f.type === "select" ? (
                                <select value={form[f.key] ?? f.default ?? ""} onChange={set(f.key)} className={inputCls} data-testid={`${testid}-field-${f.key}`}>
                                    {(typeof f.options === "function" ? f.options(form) : f.options).map((o) => <option key={o}>{o}</option>)}
                                </select>
                            ) : f.type === "textarea" ? (
                                <textarea rows={3} value={form[f.key] ?? ""} onChange={set(f.key)} className={inputCls} data-testid={`${testid}-field-${f.key}`} />
                            ) : f.type === "checkbox" ? (
                                <input type="checkbox" checked={!!form[f.key]} onChange={set(f.key)} className="h-5 w-5 accent-pink" data-testid={`${testid}-field-${f.key}`} />
                            ) : (
                                <input type={f.type || "text"} step="any" value={form[f.key] ?? ""} onChange={set(f.key)} className={inputCls} data-testid={`${testid}-field-${f.key}`} />
                            )}
                        </div>
                    ))}
                    <button onClick={save} data-testid={`${testid}-save`} className="w-full rounded-full bg-pink py-3 font-oswald uppercase tracking-widest text-sm text-ink hover:bg-ink hover:text-white transition-colors">
                        {editing ? "Save Changes" : "Add"}
                    </button>
                    {editing && (
                        <button onClick={() => setForm({})} className="w-full rounded-full border border-zinc-300 py-3 font-oswald uppercase tracking-widest text-sm text-zinc-500">
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ProductsTab = () => (
    <CrudTab path="/admin/products" listKey="name" testid="admin-products"
        subtitle={(r) => (
            <>
                <span className="text-zinc-500">${Number(r.price || 0).toFixed(2)} · {r.category}</span> {r.status && <StatusPill value={r.status} />}
                {r.wholesale_enabled && (
                    <span className="rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase bg-ink text-white">
                        Wholesale{r.wholesale_price != null ? ` $${Number(r.wholesale_price).toFixed(2)}` : ""}
                    </span>
                )}
            </>
        )}
        fields={[
        { key: "name", label: "Product Name" },
        { key: "category", label: "Category", type: "select", options: ["Cotton Candy", "Popcorn", "Party Packs", "Mystery"] },
        { key: "kind", label: "Product Type", type: "select", options: ["cotton_candy", "popcorn"] },
        { key: "description", label: "Description", type: "textarea" },
        { key: "price", label: "Price ($)", type: "number" },
        { key: "sku", label: "SKU" },
        { key: "tag", label: "Badge tag (optional)" },
        { key: "image", label: "Image URL (optional)" },
        { key: "inventory", label: "Inventory", type: "number" },
        { key: "display_order", label: "Display order", type: "number" },
        { key: "status", label: "Status", type: "select", options: PRODUCT_STATUSES, default: "HIDDEN" },
        { key: "needs_pricing", label: "Needs owner pricing (keep hidden until priced)", type: "checkbox" },
        { key: "wholesale_enabled", label: "Wholesale: show in Retailer Portal", type: "checkbox" },
        { key: "wholesale_price", label: "Wholesale price per unit ($)", type: "number" },
        { key: "wholesale_sku", label: "Wholesale SKU" },
        { key: "case_qty", label: "Case quantity (units per case)", type: "number" },
        { key: "moq", label: "Minimum order quantity (units)", type: "number" },
    ]} />
);

export const EventPackagesTab = () => (
    <CrudTab path="/admin/event-packages" listKey="name" testid="admin-event-packages" fields={[
        { key: "name", label: "Package Name" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "starting_price", label: "Starting at ($) — leave blank for pricing on request", type: "number" },
        { key: "custom_quote", label: "Custom quote (no displayed price)", type: "checkbox" },
        { key: "included_guests", label: "Included guest count" },
        { key: "included_duration", label: "Included service duration" },
        { key: "included_flavors", label: "Included flavors" },
        { key: "add_ons", label: "Available add-ons", type: "textarea" },
        { key: "display_order", label: "Display order", type: "number" },
        { key: "active", label: "Active (visible on site)", type: "checkbox" },
    ]} />
);

const ProductTypeBadge = ({ type }) => (
    <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase ${
        type === "cotton_candy" ? "bg-pink-soft text-pink-dark" : "bg-zinc-200 text-zinc-600"
    }`}>
        {type === "cotton_candy" ? "Cotton Candy" : "Popcorn"}
    </span>
);

export const FlavorsTab = () => {
    const [cats, setCats] = useState([]);
    useEffect(() => {
        api.get("/admin/flavor-categories").then((r) => setCats(r.data || [])).catch(() => {});
    }, []);
    return (
        <CrudTab path="/admin/flavors" listKey="name" testid="admin-flavors"
            subtitle={(r) => (
                <>
                    <ProductTypeBadge type={r.product_type} />
                    <span className="text-zinc-500">{r.category}</span>
                    {r.seasonal && <span className="text-pink-dark text-[9px] font-bold uppercase tracking-[0.15em]">Seasonal</span>}
                    {r.show_in_voting && <span className="rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase bg-ink text-white">In Voting</span>}
                    {r.active === false && <StatusPill value="HIDDEN" />}
                </>
            )}
            fields={[
            { key: "product_type", label: "Product Type", type: "select", options: ["cotton_candy", "popcorn"], default: "cotton_candy" },
            { key: "name", label: "Flavor Name" },
            { key: "category", label: "Category", type: "select", default: "",
              options: (form) => cats.filter((c) => c.product_type === (form.product_type || "cotton_candy")).map((c) => c.name) },
            { key: "description", label: "Description", type: "textarea" },
            { key: "color", label: "Color (hex)", default: "#FFC2D9" },
            { key: "image", label: "Image URL (optional)" },
            { key: "seasonal", label: "Seasonal", type: "checkbox" },
            { key: "display_order", label: "Display order", type: "number" },
            { key: "active", label: "Active (visible on site)", type: "checkbox" },
            { key: "show_in_voting", label: "Show in Flavor Voting (public vote + tester choices — requires Active too)", type: "checkbox" },
        ]} />
    );
};

export const CategoriesTab = () => {
    const [cats] = useFetch("/admin/flavor-categories");
    const dupes = new Set();
    const seen = new Set();
    cats.forEach((c) => {
        const k = `${c.product_type}:${c.name}`;
        if (seen.has(k)) dupes.add(k);
        seen.add(k);
    });
    return (
        <CrudTab path="/admin/flavor-categories" listKey="name" testid="admin-categories"
            subtitle={(r) => (
                <>
                    <ProductTypeBadge type={r.product_type} />
                    {dupes.has(`${r.product_type}:${r.name}`) && (
                        <span className="rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase bg-red-100 text-red-600">
                            Duplicate in same product — keep one
                        </span>
                    )}
                </>
            )}
            fields={[
            { key: "product_type", label: "Product Type", type: "select", options: ["cotton_candy", "popcorn"], default: "cotton_candy" },
            { key: "name", label: "Category Name" },
            { key: "order", label: "Display order", type: "number" },
        ]} />
    );
};

export const PacksTab = () => (
    <CrudTab path="/admin/party-packs" listKey="name" testid="admin-packs" fields={[
        { key: "name", label: "Name" },
        { key: "guests", label: "Serves (e.g. Serves up to 15)" },
        { key: "price", label: "Price ($)", type: "number" },
        { key: "contents", label: "Contents (comma separated)", type: "textarea" },
        { key: "status", label: "Status", type: "select", options: PACK_STATUSES, default: "DRAFT" },
        { key: "needs_pricing", label: "Needs owner pricing (keep in DRAFT until approved)", type: "checkbox" },
        { key: "featured", label: "Featured", type: "checkbox" },
    ]} />
);

export const FaqsTab = () => (
    <CrudTab path="/admin/faqs" listKey="question" testid="admin-faqs" fields={[
        { key: "question", label: "Question" },
        { key: "answer", label: "Answer", type: "textarea" },
        { key: "order", label: "Sort order", type: "number" },
        { key: "active", label: "Active (visible on site)", type: "checkbox" },
        { key: "needs_review", label: "Needs owner review", type: "checkbox" },
    ]} />
);

export const PagesTab = () => (
    <CrudTab path="/admin/pages" listKey="title" testid="admin-pages" fields={[
        { key: "slug", label: "Slug (privacy / terms / shipping / returns / accessibility)" },
        { key: "title", label: "Title" },
        { key: "content", label: "Content (blank line = new paragraph)", type: "textarea" },
        { key: "status", label: "Status", type: "select", options: PAGE_STATUSES, default: "DRAFT" },
        { key: "needs_review", label: "Needs owner review", type: "checkbox" },
    ]} />
);

const WHOLESALE_FIELDS = [
    ["Business Name", "business_name"], ["Contact", "contact_name"], ["Email", "email"], ["Phone", "phone"],
    ["Business Address", "business_address"], ["Business Type", "business_type"], ["Locations", "locations"],
    ["Website / Social", "website"], ["Interested In", "interest"], ["Expected Volume", "volume"], ["Message", "message"],
];

export const WholesaleTab = () => {
    const [rows, reload] = useFetch("/admin/wholesale-inquiries");
    const [viewId, setViewId] = useState(null);
    const [notes, setNotes] = useState({});

    const setStatus = async (id, status) => {
        await api.patch(`/admin/wholesale-inquiries/${id}`, { status });
        reload();
    };
    const approve = async (r) => {
        if (!window.confirm(`Approve ${r.business_name} and create their Retailer Portal login?\n\nThey'll receive an email with a secure link to set their own password.`)) return;
        try {
            const res = await api.post(`/admin/wholesale-inquiries/${r.id}/approve`);
            toast.success(`Retailer account created for ${res.data.email}. A Set Your Password link was emailed.`, { duration: 8000 });
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };
    const saveNotes = async (id) => {
        await api.patch(`/admin/wholesale-inquiries/${id}`, { notes: notes[id] ?? "" });
        toast.success("Notes saved.");
        reload();
    };
    const remove = async (r) => {
        if (!window.confirm(`Permanently delete the application from ${r.business_name}? This cannot be undone.`)) return;
        await api.delete(`/admin/wholesale-inquiries/${r.id}`);
        toast.success("Application deleted.");
        reload();
    };

    return (
        <div className="space-y-4" data-testid="admin-wholesale-tab">
            {rows.length === 0 && <p className="text-zinc-400 text-sm">No wholesale applications yet.</p>}
            {rows.map((r) => (
                <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`wholesale-row-${r.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="font-oswald uppercase text-lg">{r.business_name} {isTestRecord(r.email) && <TestPill />}</p>
                            <p className="text-xs text-zinc-500">{r.contact_name} · {r.business_type} · {r.created_at ? new Date(r.created_at).toLocaleString() : ""}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`wholesale-status-${r.id}`} className={inputCls + " w-44"}>
                                {WHOLESALE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                            {!r.retailer_id && (
                                <button onClick={() => approve(r)} data-testid={`wholesale-approve-${r.id}`} className="rounded-full bg-pink px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors">
                                    Approve & Create Login
                                </button>
                            )}
                            {r.retailer_id && (
                                <span className="rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-green-100 text-green-700" data-testid={`wholesale-account-${r.id}`}>Portal Active</span>
                            )}
                            <button onClick={() => { setViewId(viewId === r.id ? null : r.id); setNotes((n) => ({ ...n, [r.id]: r.notes || "" })); }} data-testid={`wholesale-view-${r.id}`} className={btnGhost}>View</button>
                            {VALID_EMAIL_RE.test(r.email || "") ? (
                                <a
                                    href={mailto(r.email, `Re: Wholesale Application — ${r.business_name} — Showtime Carnival Co.`)}
                                    data-testid={`wholesale-email-${r.id}`}
                                    className={btnGhost}
                                >
                                    Email
                                </a>
                            ) : (
                                <span className={btnDanger + " opacity-40 cursor-not-allowed"} data-testid={`wholesale-email-${r.id}`}>Email</span>
                            )}
                            <button onClick={() => remove(r)} data-testid={`wholesale-delete-${r.id}`} className={btnDanger}>Delete</button>
                        </div>
                    </div>
                    {viewId === r.id && (
                        <div className="mt-5 border-t border-zinc-100 pt-5">
                            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                                {WHOLESALE_FIELDS.map(([label, key]) => (
                                    <div key={key}>
                                        <dt className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400">{label}</dt>
                                        <dd className="font-medium mt-0.5 whitespace-pre-wrap">{r[key] || "—"}</dd>
                                    </div>
                                ))}
                            </dl>
                            <div className="mt-5">
                                <label className={labelCls}>Internal notes (admin only)</label>
                                <textarea
                                    rows={2}
                                    value={notes[r.id] ?? ""}
                                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                                    className={inputCls}
                                    data-testid={`wholesale-notes-${r.id}`}
                                    placeholder="Follow-up plan, pricing discussed, etc."
                                />
                                <button onClick={() => saveNotes(r.id)} data-testid={`wholesale-notes-save-${r.id}`} className="mt-2 rounded-full bg-ink text-white px-5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-pink hover:text-ink transition-colors">
                                    Save Notes
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
export const RetailersTab = () => {
    const [rows, reload] = useFetch("/admin/retailers");
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({});

    const startEdit = (r) => {
        setEditId(r.id);
        setEditForm({
            business_name: r.business_name || "", name: r.name || "",
            partner: r.partner || "", territory: r.territory || "", cohort: r.cohort || "",
        });
    };

    const saveEdit = async (id) => {
        try {
            await api.patch(`/admin/retailers/${id}`, editForm);
            toast.success("Retailer updated.");
            setEditId(null);
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const toggleActive = async (r) => {
        await api.patch(`/admin/retailers/${r.id}`, { active: r.active === false });
        toast.success(r.active === false ? "Retailer reactivated." : "Retailer deactivated — portal access blocked.");
        reload();
    };

    const sendReset = async (r) => {
        if (!window.confirm(`Send a password-reset link to ${r.email}?\n\nThey'll receive a secure single-use link to set a new password. You will never see their password.`)) return;
        try {
            await api.post(`/admin/retailers/${r.id}/send-reset`);
            toast.success(`Password reset link emailed to ${r.email}.`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-retailers-tab">
            <div className="flex justify-end">
                <button onClick={() => downloadCsv("/admin/export/retailers.csv", "retailers.csv")} data-testid="retailers-export-csv" className={btnGhost}>Export CSV (CBEDC)</button>
            </div>
            {rows.length === 0 && <p className="text-zinc-400 text-sm">No retailer accounts yet. Approve a wholesale application to create one.</p>}
            {rows.map((r) => (
                <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`retailer-row-${r.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="font-oswald uppercase text-lg">
                                {r.business_name || r.name} {isTestRecord(r.email) && <TestPill />}
                                {r.active === false && <span className="inline-block rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.15em] uppercase bg-zinc-200 text-zinc-500 align-middle ml-2">Deactivated</span>}
                            </p>
                            <p className="text-xs text-zinc-500">{r.name} · {r.email} · approved {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</p>
                            <p className="text-xs text-pink-dark mt-1 font-semibold">
                                {[r.partner, r.territory, r.cohort].filter(Boolean).join(" · ") || "No CBEDC tags yet"}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <button onClick={() => (editId === r.id ? setEditId(null) : startEdit(r))} data-testid={`retailer-edit-${r.id}`} className={btnGhost}>
                                {editId === r.id ? "Close" : "Edit CBEDC Tags"}
                            </button>
                            <button onClick={() => sendReset(r)} data-testid={`retailer-reset-${r.id}`} className={btnGhost}>Send Password Reset</button>
                            <button onClick={() => toggleActive(r)} data-testid={`retailer-toggle-${r.id}`} className={r.active === false ? btnGhost : btnDanger}>
                                {r.active === false ? "Reactivate" : "Deactivate"}
                            </button>
                        </div>
                    </div>
                    {editId === r.id && (
                        <div className="mt-5 border-t border-zinc-100 pt-5 grid sm:grid-cols-2 gap-3" data-testid={`retailer-edit-form-${r.id}`}>
                            {[["Business Name", "business_name"], ["Contact Name", "name"],
                              ["Partner (e.g. CBEDC)", "partner"], ["Territory", "territory"], ["Pilot Cohort", "cohort"]].map(([label, key]) => (
                                <div key={key}>
                                    <label className={labelCls}>{label}</label>
                                    <input value={editForm[key]} onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))} className={inputCls} data-testid={`retailer-field-${key}-${r.id}`} />
                                </div>
                            ))}
                            <div className="sm:col-span-2">
                                <button onClick={() => saveEdit(r.id)} data-testid={`retailer-save-${r.id}`} className="rounded-full bg-ink text-white px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-pink hover:text-ink transition-colors">
                                    Save Retailer
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};



const TESTER_STATUSES = ["NEW", "REVIEWING", "SELECTED", "COMPLETED", "NOT SELECTED"];
const INTEREST_LABEL = { cotton_candy: "Cotton Candy", popcorn: "Popcorn", both: "Both" };

export const ProductInterestTab = () => {
    const [view, setView] = useState("votes");
    const [votes, reloadVotes] = useFetch("/admin/flavor-votes");
    const [testers, reloadTesters] = useFetch("/admin/flavor-testers");

    const stats = useMemo(() => {
        const by = { cotton_candy: 0, popcorn: 0, both: 0 };
        const flavorCounts = {};
        votes.forEach((v) => {
            by[v.product_interest] = (by[v.product_interest] || 0) + 1;
            (v.flavor_names || []).forEach((n) => { flavorCounts[n] = (flavorCounts[n] || 0) + 1; });
        });
        const top = Object.entries(flavorCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
        return { by, top };
    }, [votes]);

    const setStatus = async (id, status) => {
        await api.patch(`/admin/flavor-testers/${id}`, { status });
        toast.success(`Status → ${status}`);
        reloadTesters();
    };
    const removeVote = async (v) => {
        if (!window.confirm("Delete this flavor vote?")) return;
        await api.delete(`/admin/flavor-votes/${v.id}`);
        toast.success("Vote deleted.");
        reloadVotes();
    };
    const removeTester = async (t) => {
        if (!window.confirm(`Permanently delete tester ${t.first_name} (${t.email})? This cannot be undone.`)) return;
        await api.delete(`/admin/flavor-testers/${t.id}`);
        toast.success("Tester deleted.");
        reloadTesters();
    };

    const statCards = [
        ["Total Flavor Votes", votes.length],
        ["Cotton Candy Interest", stats.by.cotton_candy],
        ["Popcorn Interest", stats.by.popcorn],
        ["Both", stats.by.both],
        ["Flavor Tester Applicants", testers.length],
    ];

    return (
        <div className="space-y-6" data-testid="admin-product-interest-tab">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="product-interest-stats">
                {statCards.map(([label, val]) => (
                    <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-4">
                        <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-zinc-400">{label}</p>
                        <p className="font-oswald text-3xl mt-1" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{val}</p>
                    </div>
                ))}
            </div>
            {stats.top.length > 0 && (
                <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="top-flavors">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-3">Top Flavor Interests</p>
                    <div className="flex flex-wrap gap-2">
                        {stats.top.map(([name, count]) => (
                            <span key={name} className="rounded-full bg-pink-soft text-pink-dark px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
                                {name} · {count}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                {[["votes", "Flavor Votes"], ["testers", "Flavor Testers"]].map(([key, label]) => (
                    <button key={key} onClick={() => setView(key)} data-testid={`interest-view-${key}`} className={`rounded-full px-5 py-2.5 font-oswald uppercase tracking-widest text-xs transition-colors ${view === key ? "bg-ink text-white" : "bg-white border border-zinc-300 text-zinc-600 hover:border-pink"}`}>
                        {label}
                    </button>
                ))}
                <button onClick={() => downloadCsv(view === "votes" ? "/admin/export/flavor-votes.csv" : "/admin/export/flavor-testers.csv", view === "votes" ? "flavor_votes.csv" : "flavor_testers.csv")} data-testid="interest-export-csv" className={btnGhost + " ml-auto"}>
                    Export CSV
                </button>
            </div>

            {view === "votes" && (
                <div className="space-y-3" data-testid="flavor-votes-list">
                    {votes.length === 0 && <p className="text-zinc-400 text-sm">No flavor votes yet.</p>}
                    {votes.map((v) => (
                        <div key={v.id} className="rounded-2xl border border-zinc-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`vote-row-${v.id}`}>
                            <div className="min-w-0">
                                <p className="font-oswald uppercase">{v.flavor_names?.join(", ") || "—"}</p>
                                <p className="text-xs text-zinc-500 mt-1">
                                    {INTEREST_LABEL[v.product_interest] || v.product_interest} · {v.created_at ? new Date(v.created_at).toLocaleString() : ""} · {v.source}
                                    {v.email && <> · {v.email} {isTestRecord(v.email) && <TestPill />}</>}
                                    {" · Guest List: "}{v.marketing_consent ? "Yes" : "No"}
                                </p>
                                {v.suggested_flavor && <p className="text-xs text-pink-dark mt-1">Suggested: "{v.suggested_flavor}"</p>}
                            </div>
                            <button onClick={() => removeVote(v)} data-testid={`vote-delete-${v.id}`} className={btnDanger}>Delete</button>
                        </div>
                    ))}
                </div>
            )}

            {view === "testers" && (
                <div className="space-y-3" data-testid="flavor-testers-list">
                    {testers.length === 0 && <p className="text-zinc-400 text-sm">No flavor tester applications yet.</p>}
                    {testers.map((t) => (
                        <div key={t.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`tester-row-${t.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-oswald uppercase text-lg">{t.first_name} — {t.zip} {isTestRecord(t.email) && <TestPill />}</p>
                                    <p className="text-xs text-zinc-500">{t.email} · {INTEREST_LABEL[t.product_interest] || t.product_interest} · {t.created_at ? new Date(t.created_at).toLocaleString() : ""} · {t.source}</p>
                                    {t.flavor_names?.length > 0 && <p className="text-xs text-pink-dark mt-1">Flavors: {t.flavor_names.join(", ")}</p>}
                                    {t.comment && <p className="text-xs text-zinc-400 mt-1">"{t.comment}"</p>}
                                    <p className="text-xs text-zinc-500 mt-1">Guest List consent: {t.marketing_consent ? "Yes" : "No"}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <StatusPill value={t.status} />
                                    <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} data-testid={`tester-status-${t.id}`} className={inputCls + " w-40"}>
                                        {TESTER_STATUSES.map((s) => <option key={s}>{s}</option>)}
                                    </select>
                                    <button onClick={() => removeTester(t)} data-testid={`tester-delete-${t.id}`} className={btnDanger}>Delete</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const SignupsTab = () => {
    const [rows, reload] = useFetch("/admin/signups");
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({ first_name: "", email: "" });
    const [viewId, setViewId] = useState(null);

    const consentOf = (s) => {
        const t = s.consent_at;
        if (!t) return "Not recorded";
        const d = new Date(t);
        return isNaN(d.getTime()) ? "Not recorded" : d.toLocaleString();
    };

    const startEdit = (s) => {
        setEditId(s.id);
        setEditForm({ first_name: s.first_name || "", email: s.email || "" });
    };

    const saveEdit = async (id) => {
        await api.patch(`/admin/signups/${id}`, editForm);
        toast.success("Contact updated.");
        setEditId(null);
        reload();
    };

    const setStatus = async (id, status) => {
        await api.patch(`/admin/signups/${id}`, { status });
        toast.success(status === "unsubscribed" ? "Marked unsubscribed." : "Resubscribed.");
        reload();
    };

    const remove = async (s) => {
        if (!window.confirm(`Permanently delete this contact (${s.email})? This cannot be undone.`)) return;
        await api.delete(`/admin/signups/${s.id}`);
        toast.success("Contact deleted.");
        reload();
    };

    return (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto" data-testid="admin-signups-tab">
            <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-paper text-left text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                    <tr>
                        <th className="px-5 py-3">Email</th>
                        <th className="px-5 py-3">Name</th>
                        <th className="px-5 py-3">Source</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Consent</th>
                        <th className="px-5 py-3">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((s) => (
                        <Fragment key={s.id}>
                            <tr className="border-t border-zinc-100" data-testid={`signup-row-${s.id}`}>
                                <td className="px-5 py-3 font-semibold">
                                    {editId === s.id ? (
                                        <input value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} data-testid={`signup-edit-email-${s.id}`} />
                                    ) : <>{s.email} {isTestRecord(s.email) && <TestPill />}</>}
                                </td>
                                <td className="px-5 py-3">
                                    {editId === s.id ? (
                                        <input value={editForm.first_name} onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))} className={inputCls} data-testid={`signup-edit-name-${s.id}`} />
                                    ) : (s.first_name || "—")}
                                </td>
                                <td className="px-5 py-3">{s.source}</td>
                                <td className="px-5 py-3">
                                    <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase ${
                                        (s.status || "subscribed") === "subscribed" ? "bg-pink-soft text-pink-dark" : "bg-zinc-200 text-zinc-500"
                                    }`} data-testid={`signup-status-${s.id}`}>
                                        {(s.status || "subscribed").toUpperCase()}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-zinc-400" data-testid={`signup-consent-${s.id}`}>{consentOf(s)}</td>
                                <td className="px-5 py-3">
                                    <div className="flex flex-wrap gap-2">
                                        {editId === s.id ? (
                                            <>
                                                <button onClick={() => saveEdit(s.id)} data-testid={`signup-save-${s.id}`} className="rounded-full bg-pink px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-ink">Save</button>
                                                <button onClick={() => setEditId(null)} className={btnGhost}>Cancel</button>
                                            </>
                                        ) : (
                                            <>
                                                <button onClick={() => setViewId(viewId === s.id ? null : s.id)} data-testid={`signup-view-${s.id}`} className={btnGhost}>View</button>
                                                <button onClick={() => startEdit(s)} data-testid={`signup-edit-${s.id}`} className={btnGhost}>Edit</button>
                                                {(s.status || "subscribed") === "subscribed" ? (
                                                    <button onClick={() => setStatus(s.id, "unsubscribed")} data-testid={`signup-unsubscribe-${s.id}`} className="rounded-full border border-zinc-300 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-600 hover:border-amber-400">Unsubscribe</button>
                                                ) : (
                                                    <button onClick={() => setStatus(s.id, "subscribed")} data-testid={`signup-resubscribe-${s.id}`} className="rounded-full border border-zinc-300 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-green-600 hover:border-green-400">Resubscribe</button>
                                                )}
                                                <button onClick={() => remove(s)} data-testid={`signup-delete-${s.id}`} className={btnDanger}>Delete</button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                            {viewId === s.id && (
                                <tr className="border-t border-zinc-100 bg-paper">
                                    <td colSpan={6} className="px-5 py-4 text-xs text-zinc-600 space-y-1">
                                        <p><strong>ID:</strong> {s.id}</p>
                                        <p><strong>Email:</strong> {s.email}</p>
                                        <p><strong>Name:</strong> {s.first_name || "—"}</p>
                                        <p><strong>Source:</strong> {s.source}</p>
                                        <p><strong>Status:</strong> {(s.status || "subscribed").toUpperCase()}</p>
                                        <p><strong>Consent recorded:</strong> {consentOf(s)}</p>
                                        {s.unsubscribed_at && <p><strong>Unsubscribed:</strong> {new Date(s.unsubscribed_at).toLocaleString()}</p>}
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    ))}
                </tbody>
            </table>
            {rows.length === 0 && <p className="text-zinc-400 text-sm p-5">No signups yet.</p>}
        </div>
    );
};

export const ContactsTab = () => {
    const [rows, reload] = useFetch("/admin/contact-messages");
    const [viewId, setViewId] = useState(null);

    const setStatus = async (id, status) => {
        await api.patch(`/admin/contact-messages/${id}`, { status });
        reload();
    };

    const remove = async (m) => {
        if (!window.confirm(`Permanently delete this message from ${m.name}? This cannot be undone.`)) return;
        await api.delete(`/admin/contact-messages/${m.id}`);
        toast.success("Message deleted.");
        reload();
    };

    return (
        <div className="space-y-4" data-testid="admin-contacts-tab">
            {rows.length === 0 && <p className="text-zinc-400 text-sm">No contact messages yet.</p>}
            {rows.map((m) => (
                <div key={m.id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`contact-row-${m.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <p className="font-oswald uppercase text-lg">{m.name} — {m.reason} {isTestRecord(m.email) && <TestPill />}</p>
                            <p className="text-xs text-zinc-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
                            <p className="text-xs text-zinc-400 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : "Not recorded"}</p>
                            {viewId === m.id && <p className="text-sm text-zinc-600 mt-3 whitespace-pre-wrap">{m.message}</p>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <select value={m.status || "NEW"} onChange={(e) => setStatus(m.id, e.target.value)} data-testid={`contact-status-${m.id}`} className={inputCls + " w-36"}>
                                {CONTACT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                            <button onClick={() => setViewId(viewId === m.id ? null : m.id)} data-testid={`contact-view-${m.id}`} className={btnGhost}>View</button>
                            {VALID_EMAIL_RE.test(m.email || "") ? (
                                <a
                                    href={mailto(m.email, `Re: ${m.reason} — Showtime Carnival Co.`)}
                                    data-testid={`contact-reply-${m.id}`}
                                    className={btnGhost}
                                >
                                    Reply
                                </a>
                            ) : (
                                <span className={btnDanger + " opacity-40 cursor-not-allowed"} data-testid={`contact-reply-${m.id}`}>Reply</span>
                            )}
                            <button onClick={() => remove(m)} data-testid={`contact-delete-${m.id}`} className={btnDanger}>Delete</button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export const SettingsTab = () => {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
    if (!settings) return <p className="text-zinc-400 text-sm">Loading...</p>;

    const f = settings.fulfillment || {};
    const n = settings.notifications || {};
    const b = settings.booking || {};
    const store = settings.store || {};
    const em = settings.email || {};
    const seo = settings.seo || {};

    const setGroup = (group, k, v) => setSettings((s) => ({ ...s, [group]: { ...(s[group] || {}), [k]: v } }));
    const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));
    const toC = (v) => Math.round((parseFloat(v) || 0) * 100);
    const toD = (c) => (c ? (c / 100).toString() : "");

    const save = async () => {
        setSaving(true);
        try {
            const { email_status, stripe_mode, stripe_status, ...clean } = settings;
            await api.post("/admin/settings", clean);
            toast.success("All settings saved.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const card = "rounded-2xl border border-zinc-200 bg-white p-6 space-y-4";
    const h = "font-oswald uppercase text-lg";
    const moneyField = (group, key, label) => (
        <div>
            <label className={labelCls}>{label}</label>
            <input type="number" step="0.01" min="0" value={toD((settings[group] || {})[key])} onChange={(e) => setGroup(group, key, toC(e.target.value))} className={inputCls} placeholder="0.00" />
        </div>
    );

    return (
        <div className="pb-24" data-testid="admin-settings-tab">
            <div className="grid gap-6 lg:grid-cols-2">
                <div className={card}>
                    <p className={h}>Business</p>
                    <div><label className={labelCls}>Business display name</label><input value={settings.business_name || ""} onChange={setS("business_name")} className={inputCls} data-testid="settings-business-name" /></div>
                    <div><label className={labelCls}>Customer-facing business email</label><input value={settings.business_email || ""} onChange={setS("business_email")} className={inputCls} data-testid="settings-business-email" placeholder="hello@showtimecarnival.co" /></div>
                    <div><label className={labelCls}>Business phone</label><input value={settings.business_phone || ""} onChange={setS("business_phone")} className={inputCls} data-testid="settings-business-phone" /></div>
                    <div><label className={labelCls}>Business address (internal — never shown publicly)</label><input value={settings.business_address || ""} onChange={setS("business_address")} className={inputCls} data-testid="settings-business-address" /></div>
                    <div><label className={labelCls}>Service area</label><input value={settings.service_area || ""} onChange={setS("service_area")} className={inputCls} data-testid="settings-service-area" /></div>
                </div>
                <div className={card}>
                    <p className={h}>Notification Recipients</p>
                    <p className="text-xs text-zinc-400">One email, or several separated by commas. Use the same address everywhere or split by type.</p>
                    {[["orders", "New Orders"], ["bookings", "New Booking Requests"], ["wholesale", "New Wholesale Applications"], ["messages", "New Messages"]].map(([k, label]) => (
                        <div key={k}>
                            <label className={labelCls}>{label}</label>
                            <input value={n[k] || ""} onChange={(e) => setGroup("notifications", k, e.target.value)} className={inputCls} placeholder="owner@example.com" data-testid={`settings-notify-${k}`} />
                        </div>
                    ))}
                </div>
                <div className={card}>
                    <p className={h}>Local Delivery</p>
                    <label className="flex items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" checked={!!f.local_delivery} onChange={(e) => setGroup("fulfillment", "local_delivery", e.target.checked)} className="h-5 w-5 accent-pink" data-testid="settings-local-delivery-toggle" />
                        Local delivery enabled
                    </label>
                    <div><label className={labelCls}>Eligible ZIP codes (comma separated)</label><input value={f.local_delivery_zips || ""} onChange={(e) => setGroup("fulfillment", "local_delivery_zips", e.target.value)} className={inputCls} placeholder="Owner to enter" data-testid="settings-local-zips" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        {moneyField("fulfillment", "local_delivery_fee_cents", "Delivery fee ($)")}
                        {moneyField("fulfillment", "local_delivery_min_cents", "Minimum order ($, optional)")}
                    </div>
                    {moneyField("fulfillment", "local_delivery_free_over_cents", "Free delivery over ($, optional)")}
                    <div><label className={labelCls}>Estimated timing message</label><input value={f.local_delivery_timing || ""} onChange={(e) => setGroup("fulfillment", "local_delivery_timing", e.target.value)} className={inputCls} placeholder="Owner to enter" /></div>
                    <div><label className={labelCls}>Customer-facing delivery instructions</label><textarea rows={2} value={f.local_delivery_note || ""} onChange={(e) => setGroup("fulfillment", "local_delivery_note", e.target.value)} className={inputCls} /></div>
                </div>
                <div className={card}>
                    <p className={h}>Shipping</p>
                    <label className="flex items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" checked={!!f.shipping} onChange={(e) => setGroup("fulfillment", "shipping", e.target.checked)} className="h-5 w-5 accent-pink" data-testid="settings-shipping-toggle" />
                        Shipping enabled
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        {moneyField("fulfillment", "shipping_fee_cents", "Flat shipping fee ($)")}
                        {moneyField("fulfillment", "shipping_free_over_cents", "Free shipping over ($, optional)")}
                    </div>
                    <div><label className={labelCls}>Availability / service area</label><input value={f.shipping_availability || ""} onChange={(e) => setGroup("fulfillment", "shipping_availability", e.target.value)} className={inputCls} placeholder="Owner to enter" /></div>
                    <div><label className={labelCls}>Processing-time message</label><input value={f.shipping_processing || ""} onChange={(e) => setGroup("fulfillment", "shipping_processing", e.target.value)} className={inputCls} placeholder="Owner to enter" /></div>
                    <div><label className={labelCls}>Customer-facing shipping message</label><textarea rows={2} value={f.shipping_note || ""} onChange={(e) => setGroup("fulfillment", "shipping_note", e.target.value)} className={inputCls} /></div>
                    <p className="text-[11px] text-zinc-400">Pickup is currently OFF and hidden from customers. The option remains in the system for possible future use.</p>
                </div>
                <div className={card}>
                    <p className={h}>Booking</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Minimum lead time (days)</label><input type="number" min="0" value={b.min_lead_days || 0} onChange={(e) => setGroup("booking", "min_lead_days", parseInt(e.target.value, 10) || 0)} className={inputCls} data-testid="settings-lead-days" /></div>
                        <div><label className={labelCls}>Request number prefix</label><input value={b.request_prefix || "BK"} onChange={(e) => setGroup("booking", "request_prefix", e.target.value.toUpperCase())} className={inputCls} /></div>
                    </div>
                    <div><label className={labelCls}>Default event duration (optional)</label><input value={b.default_duration || ""} onChange={(e) => setGroup("booking", "default_duration", e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Booking disclaimer (shown + emailed)</label><textarea rows={2} value={b.disclaimer || ""} onChange={(e) => setGroup("booking", "disclaimer", e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Customer confirmation message</label><textarea rows={2} value={b.confirmation_message || ""} onChange={(e) => setGroup("booking", "confirmation_message", e.target.value)} className={inputCls} /></div>
                    <p className="text-[11px] text-zinc-400">Booking stays request-based. Requests never auto-confirm availability.</p>
                </div>
                <div className={card}>
                    <p className={h}>Store & Payments</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Currency</label><input value={store.currency || "usd"} onChange={(e) => setGroup("store", "currency", e.target.value.toLowerCase())} className={inputCls} /></div>
                        <div><label className={labelCls}>Order number prefix</label><input value={store.order_prefix || "SC"} onChange={(e) => setGroup("store", "order_prefix", e.target.value.toUpperCase())} className={inputCls} /></div>
                    </div>
                    <div><label className={labelCls}>Tax configuration note</label><input value={store.tax_note || ""} onChange={(e) => setGroup("store", "tax_note", e.target.value)} className={inputCls} /></div>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="font-bold tracking-[0.15em] uppercase text-zinc-500">Stripe:</span>
                        <StatusPill value={settings.stripe_status || "NOT CONFIGURED"} />
                        <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase ${settings.stripe_mode === "LIVE MODE" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="settings-stripe-mode">{settings.stripe_mode}</span>
                    </div>
                    <p className="text-[11px] text-zinc-400">Stripe keys are stored as server environment variables and never shown here.</p>
                </div>
                <div className={card}>
                    <p className={h}>Email</p>
                    <div><label className={labelCls}>Sender name</label><input value={em.from_name || ""} onChange={(e) => setGroup("email", "from_name", e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Reply-to email</label><input value={em.reply_to || ""} onChange={(e) => setGroup("email", "reply_to", e.target.value)} className={inputCls} placeholder="owner-controlled inbox" /></div>
                    <div className="flex items-center gap-2 text-xs">
                        <span className="font-bold tracking-[0.15em] uppercase text-zinc-500">Status:</span>
                        <span className={`inline-block rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase ${settings.email_status === "CONFIGURED" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="settings-email-status">{settings.email_status}</span>
                    </div>
                    <p className="text-[11px] text-zinc-400">Email service credentials stay server-side. The sender email address is managed by the email provider.</p>
                </div>
                <div className={card}>
                    <p className={h}>Social</p>
                    <div><label className={labelCls}>Instagram URL</label><input value={settings.instagram_url || ""} onChange={setS("instagram_url")} className={inputCls} data-testid="settings-instagram" placeholder="https://instagram.com/..." /></div>
                    <div><label className={labelCls}>TikTok URL</label><input value={settings.tiktok_url || ""} onChange={setS("tiktok_url")} className={inputCls} data-testid="settings-tiktok" placeholder="https://tiktok.com/@..." /></div>
                    <p className="text-[11px] text-zinc-400">More social links can be added later without changing this section.</p>
                </div>
                <div className={card}>
                    <p className={h}>Website / SEO</p>
                    <div><label className={labelCls}>Site title</label><input value={seo.site_title || ""} onChange={(e) => setGroup("seo", "site_title", e.target.value)} className={inputCls} data-testid="settings-site-title" /></div>
                    <div><label className={labelCls}>Meta description</label><textarea rows={2} value={seo.meta_description || ""} onChange={(e) => setGroup("seo", "meta_description", e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Social sharing image URL</label><input value={seo.social_image || ""} onChange={(e) => setGroup("seo", "social_image", e.target.value)} className={inputCls} /></div>
                    <div><label className={labelCls}>Domain status</label><input value={seo.domain_status || ""} onChange={(e) => setGroup("seo", "domain_status", e.target.value)} className={inputCls} /></div>
                </div>
            </div>
            <div className="sticky bottom-4 mt-8">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="settings-save"
                    className="w-full md:w-auto md:min-w-[320px] rounded-full bg-pink px-10 py-4 font-oswald uppercase tracking-widest text-ink shadow-xl hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
                >
                    {saving ? "Saving..." : "Save All Settings"}
                </button>
            </div>
        </div>
    );
};
